// ============================================================================
// Central visibility gate for member / helper "Todos"-style feeds.
// ----------------------------------------------------------------------------
// A checklist appears for a user when ALL of:
//   1. Assigned to them (assigneeId matches)
//   2. Not masked out by helper-visibility rules
//   3. Workflow status is one the user can act on (ASSIGNED / ACCEPTED /
//      ASSESSING / ONGOING) — completed / postponed / not-required hide.
//   4. NEW: time-derived status is 'active' — upcoming and archived items
//      never appear in the Todos feed. Upcoming items surface early when
//      the workspace admin has set `todosLeadDays` > 0.
// ============================================================================
import type { ChecklistItem, ChecklistWorkflowStatus } from '../types/schema'

export const ACTIVE_WORKFLOW_STATUSES = new Set<ChecklistWorkflowStatus>([
  'ASSIGNED',
  'ACCEPTED',
  'ASSESSING',
  'ONGOING',
])

export const isActiveWorkflowStatus = (
  status: ChecklistWorkflowStatus | null | undefined,
): boolean => !!status && ACTIVE_WORKFLOW_STATUSES.has(status)

export type VisibilityCandidate = Pick<
  ChecklistItem,
  'assigneeId' | 'isHelperVisible' | 'workflowStatus' | 'timeStatus'
> & {
  /** Some feeds normalize a fallback status (e.g. helperAssignedItemsService
   *  coerces missing status to ASSIGNED). Pass that here to preserve their
   *  semantics without duplicating the coercion. */
  effectiveStatus?: ChecklistWorkflowStatus
}

/**
 * Broad visibility check: matches assignee + role visibility only.
 * Does not gate on workflow status or schedule window.
 */
export const isChecklistAssignedToUser = (
  item: VisibilityCandidate,
  uid: string,
): boolean => {
  if (item.assigneeId !== uid) return false
  if (item.isHelperVisible === false) return false
  return true
}

/**
 * Narrow visibility check: additionally requires active workflow + active schedule.
 * Used for "today's todos" / actionable feeds.
 */
export const isChecklistCurrentlyActionable = (
  item: VisibilityCandidate,
  uid: string,
): boolean => {
  if (!isChecklistAssignedToUser(item, uid)) return false
  const status = item.effectiveStatus ?? item.workflowStatus ?? null
  if (!isActiveWorkflowStatus(status)) return false
  // timeStatus defaults to 'active' when unset (backward compatibility for
  // items without schedule fields) — treat missing the same as 'active'.
  const time = item.timeStatus ?? 'active'
  return time === 'active'
}

/**
 * Single truth for "should this checklist show in the Todos feed for uid?"
 * Backward-compatible alias for isChecklistCurrentlyActionable.
 */
export const isChecklistVisibleToAssignee = isChecklistCurrentlyActionable
